import { cn } from '@/_lib/utils'
import { env } from '@/env'
import { useLocation } from '@tanstack/react-router'
import { Building2, Hospital, LogOut, PanelLeftClose, Percent, Users } from 'lucide-react'
import { SIDEBAR_NAV_ITEMS, type NavItem } from '../_constants/navigation'
import { useSession } from '../_hooks/use-session'
import { authClient } from '../_lib/auth-client'
import { useCompanyStore, useSidebarStore } from '../_stores'
import { LogoIcon } from './icons/logo-icon'
import { LogoText } from './icons/logo-text'
import {
  Sidebar as _Sidebar,
  SidebarButton,
  SidebarButtonImage,
  SidebarButtonText,
  SidebarContent,
  SidebarHeader,
  SidebarItems,
} from './ui/sidebar'

const LUCIDE_ICONS = { Percent, Hospital, Building2, Users } as const

function NavItemIcon({ item, isActive }: { item: NavItem; isActive: boolean }) {
  if (item.icon.type === 'image') {
    return (
      <SidebarButtonImage src={isActive ? item.icon.active : item.icon.default} alt={item.label} />
    )
  }

  const Icon = LUCIDE_ICONS[item.icon.name]
  return (
    <Icon
      className={`shrink-0 ${isActive ? 'text-brand-green-primary-5' : 'text-brand-grayscale-7'}`}
      size={20}
    />
  )
}

export const Sidebar = () => {
  const { data: session } = useSession()
  const { pathname } = useLocation()
  const { isCollapsed, toggle } = useSidebarStore()

  const userType = session?.user.userType
  const companySlug = useCompanyStore((s) => s.company?.slug)
  const collapsedItemClass = isCollapsed ? 'justify-center px-2' : undefined

  const handleSignOutClick = async () => {
    await authClient.signOut()
    window.location.href = `${env.VITE_DOMAIN_URL}/login`
  }

  return (
    <_Sidebar className={isCollapsed ? 'w-[72px]' : 'w-[240px]'}>
      <SidebarHeader
        className={isCollapsed ? 'items-center justify-center !px-0' : 'justify-between'}
      >
        {isCollapsed ? (
          <LogoIcon />
        ) : (
          <>
            <LogoText />
            <button
              aria-label="Recolher menu"
              className="shrink-0 rounded-md p-1.5 text-brand-grayscale-7 transition-colors hover:bg-brand-grayscale-3"
              onClick={toggle}
            >
              <PanelLeftClose size={20} />
            </button>
          </>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarItems className={isCollapsed ? 'items-center px-2' : undefined}>
          {SIDEBAR_NAV_ITEMS.filter((item) => userType && item.allowedFor.includes(userType)).map(
            (item) => {
              const href = typeof item.href === 'function' ? item.href({ companySlug }) : item.href
              if (!href) return null
              const isActive = pathname.startsWith(href)
              return (
                <SidebarButton
                  key={href}
                  href={href}
                  isActive={isActive}
                  className={collapsedItemClass}
                >
                  <NavItemIcon item={item} isActive={isActive} />
                  {!isCollapsed && <SidebarButtonText>{item.label}</SidebarButtonText>}
                </SidebarButton>
              )
            },
          )}
        </SidebarItems>

        <div className={`flex flex-col gap-0.5 pb-4 ${isCollapsed ? 'items-center px-2' : 'px-4'}`}>
          <button
            className={cn(
              'flex w-full items-center gap-3 rounded-md py-2.5 text-[15px] font-medium text-brand-grayscale-7 transition-colors hover:bg-brand-grayscale-3',
              isCollapsed ? 'justify-center px-2' : 'px-3',
            )}
            onClick={handleSignOutClick}
          >
            <LogOut className="shrink-0" size={20} />
            {!isCollapsed && 'Sair'}
          </button>
        </div>
      </SidebarContent>
    </_Sidebar>
  )
}
